import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Full-screen signature pad using native canvas.
 * Supports touch + mouse, and a "client refusés to sign" flow.
 *
 * @param {{ signerName: string, onConfirm: (data: {signatureData: string, signerName: string}) => void, onCancel: () => void }} props
 */
export default function SignaturePad({ signerName: initialName = '', onConfirm, onCancel }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [name, setName] = useState(initialName);
  const [refusalMode, setRefusalMode] = useState(false);
  const [refusalReason, setRefusalReason] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e3a5f';
  }, []);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasStrokes(true);
  }, [drawing, getPos]);

  const endDraw = useCallback((e) => {
    if (e) e.preventDefault();
    setDrawing(false);
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  }, []);

  const confirm = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStrokes) return;
    const signatureData = canvas.toDataURL('image/png');
    onConfirm({ signatureData, signerName: name.trim() || 'Non renseigné' });
  }, [hasStrokes, name, onConfirm]);

  const confirmRefusal = useCallback(() => {
    if (!refusalReason.trim()) return;
    onConfirm({
      signatureData: 'REFUSED',
      signerName: `REFUS: ${refusalReason.trim()}`,
    });
  }, [refusalReason, onConfirm]);

  // Refusal form
  if (refusalMode) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        <div className="bg-red-700 text-white px-4 py-3 flex items-center justify-between">
          <h2 className="font-bold">Refus de signature</h2>
          <button onClick={() => setRefusalMode(false)} className="text-sm underline opacity-80">Retour</button>
        </div>
        <div className="flex-1 px-4 py-6 space-y-4">
          <p className="text-gray-700">Le client refuse de signer. Indiquez le motif :</p>
          <textarea
            value={refusalReason}
            onChange={(e) => setRefusalReason(e.target.value)}
            placeholder="Motif du refus (obligatoire)"
            className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 h-32 resize-none text-lg focus:border-red-500 outline-none"
            autoFocus
          />
          {!refusalReason.trim() && (
            <p className="text-sm text-red-500">Le motif est obligatoire</p>
          )}
        </div>
        <div className="px-4 py-3 flex gap-3">
          <button
            onClick={() => setRefusalMode(false)}
            className="flex-1 border border-gray-300 rounded-xl py-3 font-medium"
          >
            Annuler
          </button>
          <button
            onClick={confirmRefusal}
            disabled={!refusalReason.trim()}
            className="flex-1 bg-red-600 text-white rounded-xl py-3 font-medium disabled:opacity-40"
          >
            Confirmer le refus
          </button>
        </div>
      </div>
    );
  }

  // Normal signature pad
  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <div className="bg-blue-800 text-white px-4 py-3 flex items-center justify-between">
        <h2 className="font-bold">Signature client</h2>
        <button onClick={onCancel} className="text-sm underline opacity-80">Annuler</button>
      </div>

      <div className="bg-blue-50 border-b border-blue-100 px-4 py-2.5">
        <p className="text-blue-800 text-sm font-medium text-center">Présentez l'écran au client pour qu'il signe</p>
      </div>

      <div className="px-4 py-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nom du signataire"
          className="w-full border-b-2 border-gray-300 py-2 text-lg focus:border-blue-600 outline-none"
        />
      </div>

      <div className="flex-1 px-4 py-2">
        <div className="w-full h-full border-2 border-dashed border-gray-300 rounded-xl relative">
          <canvas
            ref={canvasRef}
            className="w-full h-full touch-none"
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          {!hasStrokes && (
            <p className="absolute inset-0 flex items-center justify-center text-gray-400 text-lg pointer-events-none">
              Signez ici
            </p>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-2">
        <div className="flex gap-3">
          <button onClick={clear} className="flex-1 border border-gray-300 rounded-xl py-3 font-medium">
            Effacer
          </button>
          <button
            onClick={confirm}
            disabled={!hasStrokes}
            className="flex-1 bg-blue-600 text-white rounded-xl py-3 font-medium disabled:opacity-40"
          >
            Valider
          </button>
        </div>
        <button
          onClick={() => setRefusalMode(true)}
          className="w-full text-red-600 text-sm underline py-1"
        >
          Client refusé de signer
        </button>
      </div>
    </div>
  );
}
